import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useNavigate } from 'react-router-dom';

interface AdminUser {
  uid: string;
  name: string;
  email: string;
  phoneNumber?: string;
  userLevel: number;
  permissions: {
    users: boolean;
    facilities: boolean;
    facilityReservations: boolean;
    portville: boolean;
    portvilleRooms: boolean;
    parking: boolean;
    notices: boolean;
  };
  smsNotifications?: {
    facilityReservations: boolean;
  };
}

const defaultPermissions = {
  users: false,
  facilities: false,
  facilityReservations: false,
  portville: false,
  portvilleRooms: false,
  parking: false,
  notices: false
};

const defaultSmsNotifications = {
  facilityReservations: false
};

const AdminManagePage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, userLevel, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // 인증 로딩이 완료된 후에만 권한 체크
    if (!authLoading) {
      // 최고관리자만 접근 가능
      if (!user || userLevel !== 1) {
        navigate('/admin/dashboard');
        return;
      }

      const fetchAdmins = async () => {
        try {
          const q = query(
            collection(db, 'user_profiles'),
            where('userLevel', '==', 2) // userLevel 2인 관리자만 조회
          );
          const querySnapshot = await getDocs(q);
          const adminList = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              uid: doc.id,
              name: data.name || '',
              email: data.email || '',
              phoneNumber: data.phoneNumber || '',
              userLevel: data.userLevel || 0,
              permissions: data.permissions || defaultPermissions,
              smsNotifications: data.smsNotifications || defaultSmsNotifications
            };
          }) as AdminUser[];
          setAdmins(adminList);
        } catch (error) {
          console.error('관리자 목록 조회 실패:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchAdmins();
    }
  }, [user, userLevel, navigate, authLoading]);

  const handlePermissionChange = async (
    adminId: string,
    permission: keyof AdminUser['permissions'],
    value: boolean
  ) => {
    try {
      const adminRef = doc(db, 'user_profiles', adminId);
      await updateDoc(adminRef, {
        [`permissions.${permission}`]: value
      });

      const syncAdminClaims = httpsCallable(functions, 'syncAdminClaims');
      await syncAdminClaims({ uid: adminId });

      setAdmins(prevAdmins =>
        prevAdmins.map(admin =>
          admin.uid === adminId
            ? {
                ...admin,
                permissions: {
                  ...admin.permissions,
                  [permission]: value
                }
              }
            : admin
        )
      );
    } catch (error) {
      console.error('권한 변경 실패:', error);
    }
  };

  const handleSmsNotificationChange = async (
    adminId: string,
    notificationType: 'facilityReservations',
    value: boolean
  ) => {
    try {
      const adminRef = doc(db, 'user_profiles', adminId);
      await updateDoc(adminRef, {
        [`smsNotifications.${notificationType}`]: value
      });

      setAdmins(prevAdmins =>
        prevAdmins.map(admin =>
          admin.uid === adminId
            ? {
                ...admin,
                smsNotifications: {
                  ...(admin.smsNotifications || defaultSmsNotifications),
                  [notificationType]: value
                }
              }
            : admin
        )
      );
    } catch (error) {
      console.error('SMS 알림 설정 변경 실패:', error);
    }
  };

  if (loading || authLoading) {
    return <div>로딩 중...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">관리자 관리</h1>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                이름
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                이메일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                사용자 관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                시설물 관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                시설물 예약 관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                포트빌 입주 신청 관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                포트빌 호실관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                주차장 관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                공지사항 관리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                시설물 예약 SMS 알림
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.map(admin => (
              <tr key={admin.uid}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{admin.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.users}
                    onChange={e => handlePermissionChange(admin.uid, 'users', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.facilities}
                    onChange={e => handlePermissionChange(admin.uid, 'facilities', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.facilityReservations}
                    onChange={e => handlePermissionChange(admin.uid, 'facilityReservations', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.portville}
                    onChange={e => handlePermissionChange(admin.uid, 'portville', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.portvilleRooms}
                    onChange={e => handlePermissionChange(admin.uid, 'portvilleRooms', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.parking}
                    onChange={e => handlePermissionChange(admin.uid, 'parking', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={admin.permissions.notices}
                    onChange={e => handlePermissionChange(admin.uid, 'notices', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col items-center space-y-1">
                    <input
                      type="checkbox"
                      checked={admin.smsNotifications?.facilityReservations || false}
                      onChange={e => handleSmsNotificationChange(admin.uid, 'facilityReservations', e.target.checked)}
                      disabled={!admin.phoneNumber}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      title={!admin.phoneNumber ? '전화번호가 등록되지 않은 관리자입니다' : ''}
                    />
                    {!admin.phoneNumber && (
                      <span className="text-xs text-gray-400">전화번호 없음</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminManagePage; 
